<script setup lang="ts">
import { computed } from 'vue'
import Button from '@uiKit/elements/Button.vue'
import Card from '@uiKit/components/Card.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Chip from '@uiKit/elements/Chip.vue'
import { formatDate } from '@shared/utils/dateTimeUtils'
import { formatFeeCalculation, formatFeeValue, formatVatRate } from '@shared/utils/feeUtils'
import {
  FEE_LIFECYCLE_STAGE_LABEL,
  FEE_PARTY_ROLE_LABEL,
  FEE_RECURRENCE_LABEL,
  FEE_VALUE_TYPE_LABEL,
} from '@shared/constants/fees'
import type { SelectOption } from '@shared/types'
import type { FeeDefinition } from '../types/feeTypes'

const props = defineProps<{
  fee: FeeDefinition
  /** Every market a fee can be scoped to, `{ label: name, value: id }`. */
  markets: SelectOption[]
  isDisabling: boolean
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'edit'): void
  (e: 'viewHistory'): void
  (e: 'disable'): void
}>()

// A future `validFrom` means this version has not started applying yet — the
// previous version was already closed, so nothing else is in force meanwhile.
const isScheduled = (): boolean => new Date(props.fee.validFrom).getTime() > Date.now()

// The market this fee is scoped to, named rather than described. A null id is
// the system default every market inherits; an id the market list does not
// carry (a market since removed) falls back to the generic wording.
const scopeLabel = computed<string>(() => {
  if (props.fee.marketId === null) return 'All markets'
  const market = props.markets.find((option) => option.value === props.fee.marketId)
  return market ? String(market.label) : 'Market-specific'
})
</script>

<template>
  <Box class="animate-fade-in">
    <Box flex class="justify-content-between align-items-center flex-wrap gap-4 mb-5">
      <Box flex class="align-items-center gap-3">
        <Button
          icon="pi pi-arrow-left"
          variant="text"
          class="flex-shrink-0"
          @click="emit('back')"
        />
        <Box>
          <Text type="page-title">{{ fee.name }}</Text>
          <Box flex class="align-items-center gap-2 mt-1">
            <Text type="body" class="fee-code">{{ fee.code }}</Text>
            <Text type="body" color="muted">•</Text>
            <Chip
              :label="fee.isActive ? 'ACTIVE' : 'DISABLED'"
              :color="fee.isActive ? 'success' : 'warning'"
              variant="tonal"
              size="small"
              dot
            />
          </Box>
        </Box>
      </Box>
      <Box flex class="gap-2 flex-shrink-0">
        <Button
          label="View History"
          icon="pi pi-history"
          variant="outline"
          severity="secondary"
          @click="emit('viewHistory')"
        />
        <Button
          v-if="fee.isActive"
          label="Disable Fee"
          icon="pi pi-ban"
          variant="outline"
          severity="danger"
          :loading="isDisabling"
          @click="emit('disable')"
        />
        <Button label="Edit Fee" icon="pi pi-pencil" @click="emit('edit')" />
      </Box>
    </Box>

    <Box v-if="isScheduled()" flex class="gap-4 mb-5 scheduled-banner">
      <Box class="banner-icon flex-shrink-0">
        <PiIcon name="clock" class="text-2xl banner-icon-color" />
      </Box>
      <Box class="flex-1">
        <Text type="subheading" class="banner-title">Takes Effect Later</Text>
        <Text type="body" class="mt-2 mb-0 line-height-3">
          This version applies from <strong>{{ formatDate(fee.validFrom) }}</strong
          >. Fees settled before that date stay explained by the version it replaced — open the
          history to read it.
        </Text>
      </Box>
    </Box>

    <Box class="grid">
      <Box class="col-12 md:col-8">
        <Card variant="elevated" class="details-card">
          <Text type="caption" class="section-label mb-4">Fee Attributes</Text>

          <Box class="grid">
            <Box class="col-12 md:col-6 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Lifecycle Stage
              </Text>
              <Text type="body" weight="bold">
                {{ FEE_LIFECYCLE_STAGE_LABEL[fee.lifecycleStage] }}
              </Text>
            </Box>
            <Box class="col-12 md:col-6 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Recurrence
              </Text>
              <Text type="body" weight="bold">{{ FEE_RECURRENCE_LABEL[fee.recurrence] }}</Text>
            </Box>
            <Box class="col-12 md:col-6 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Paid By
              </Text>
              <Text type="body" weight="bold">{{ FEE_PARTY_ROLE_LABEL[fee.paidByRole] }}</Text>
            </Box>
            <Box class="col-12 md:col-6 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Paid To
              </Text>
              <Text type="body" weight="bold">{{ FEE_PARTY_ROLE_LABEL[fee.paidToRole] }}</Text>
            </Box>
            <Box v-if="fee.description" class="col-12 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                What It Covers
              </Text>
              <Text type="body" class="line-height-3">{{ fee.description }}</Text>
            </Box>
            <Box class="col-12 md:col-4 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Value Type
              </Text>
              <Text type="body" weight="bold">{{ FEE_VALUE_TYPE_LABEL[fee.valueType] }}</Text>
            </Box>
            <Box class="col-12 md:col-4 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Value In Force
              </Text>
              <Text type="heading" class="fee-value">
                {{ formatFeeValue(fee.defaultValue, fee.valueType, fee.unitBasis) }}
              </Text>
            </Box>
            <Box class="col-12 md:col-4 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                VAT
              </Text>
              <Text type="body" weight="bold">
                {{ formatVatRate(fee.isVatApplicable, fee.vatPercentage) }}
              </Text>
            </Box>
            <Box class="col-12 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Calculation
              </Text>
              <Text type="body" class="calculation-badge">
                {{
                  formatFeeCalculation({
                    value: fee.defaultValue,
                    valueType: fee.valueType,
                    valueBasis: fee.valueBasis,
                    unitBasis: fee.unitBasis,
                  })
                }}
              </Text>
            </Box>
            <Box class="col-12 md:col-6 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                In Force Since
              </Text>
              <Text type="body" weight="bold">{{ formatDate(fee.validFrom) }}</Text>
            </Box>
            <Box class="col-12 md:col-6 mb-4">
              <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
                Scope
              </Text>
              <Text type="body" weight="bold">{{ scopeLabel }}</Text>
            </Box>
          </Box>
        </Card>
      </Box>

      <Box class="col-12 md:col-4">
        <Box class="governance-card">
          <Text type="caption" class="section-label mb-4">Platform Governance</Text>
          <Box flex class="flex-column gap-3">
            <Box flex class="gap-2">
              <PiIcon name="history" class="text-base mt-1 governance-icon" />
              <Text type="body" color="muted" class="line-height-3">
                Fees are <strong>versioned, never edited</strong>. Saving a change closes this
                version and opens a new one, so a fee already settled stays explainable from the
                version that produced it.
              </Text>
            </Box>
            <Box flex class="gap-2">
              <PiIcon
                :name="fee.isCustomisable ? 'pencil' : 'lock'"
                class="text-base mt-1 governance-icon"
              />
              <Text type="body" color="muted" class="line-height-3">
                {{
                  fee.isCustomisable
                    ? 'A property may override this value on its own financials screen.'
                    : 'This value is fixed — no property may depart from it.'
                }}
              </Text>
            </Box>
            <Box flex class="gap-2">
              <PiIcon name="ban" class="text-base mt-1 text-muted" />
              <Text type="body" color="muted" class="line-height-3">
                Disabling a fee never deletes it — settled charges keep pointing at their
                definition.
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.text-muted {
  color: var(--p-text-muted-color);
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-label {
  display: block;
}

.field-label {
  display: block;
  margin-bottom: 0.25rem;
}

.fee-code {
  font-family: var(--font-mono, monospace);
  color: var(--p-text-muted-color);
}

.governance-card {
  background: var(--p-surface-50);
  border: 1px solid var(--p-surface-200);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
}

.governance-icon {
  color: var(--p-primary-500);
}

.calculation-badge {
  display: inline-block;
  background: var(--p-primary-50);
  padding: 0.25rem 0.75rem;
  border-radius: var(--p-border-radius-md);
}

.scheduled-banner {
  background: var(--p-warning-50);
  border: 1px solid var(--p-warning-100);
  border-left: 6px solid var(--p-warning-500);
  padding: 1.5rem;
  border-radius: 0.75rem;
}

.banner-icon {
  width: 48px;
  height: 48px;
  background: var(--p-warning-100);
  border-radius: var(--p-border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-icon-color {
  color: var(--p-warning-600);
}
</style>
